/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Expandable, { ExpandableRef } from '@ui/components/Expandable/Expandable';
import { Anchor, Divider, Stack } from '@mantine/core';
import { usePrevious } from '@mantine/hooks';
import Text from '@ui/components/Text/Text';

import { getProject } from 'src/selectors/projects';
import { getMainAssetsUploadsForProject } from 'src/selectors/assetUploads';
import { getMainFolderUploadsForProject } from 'src/selectors/folderUploads';
import FolderUpload from 'src/entities/folderUpload';
import AssetUpload from 'src/entities/assetUpload';
import { cancelAssetUpload } from 'src/actions/assetUploads';
import { ASSET_UPLOAD_ERRORS, ITEM_UPLOAD_STATUS } from 'src/services/DirectFilesUploaderCoordinator';
import useRequestUpgradeRoleMutation from '@projects/queries/useRequestUpgradeRoleMutation';
import { UPGRADE_REQUEST_SOURCES } from 'src/new_arch/modules/projectView/components/OverviewPermissionTooltip/upgradeRequestSources';

import UploadItem from './UploadItem/UploadItem';

type ProjectUploadsListProps = {
	projectID: number
};

const hasActiveStatus = ( upload: AssetUpload | FolderUpload ) => (
	[ ITEM_UPLOAD_STATUS.UPLOADING, ITEM_UPLOAD_STATUS.WAITING ].includes( upload.status )
);

const ProjectUploadsList = ( { projectID }: ProjectUploadsListProps ) => {
	const expandableRef = useRef<ExpandableRef>( null );

	// @ts-ignore
	const project = useSelector( state => getProject( state, { projectID } ) );
	const assetUploads: AssetUpload[] = useSelector(
		// @ts-ignore
		state => getMainAssetsUploadsForProject( state, { projectID } )
	);
	const folderUploads: FolderUpload[] = useSelector(
		// @ts-ignore
		state => getMainFolderUploadsForProject( state, { projectID } )
	);

	const uploads = useMemo( () => (
		[ ...folderUploads, ...assetUploads ].sort( ( a, b ) => {
			const timeDiff = b.createdAt.getTime() - a.createdAt.getTime();
			if ( timeDiff !== 0 ) { return timeDiff; }

			return a.statusOrder - b.statusOrder;
		} )
	), [ assetUploads, folderUploads ] );
	const hasActiveUploads = useMemo( () => uploads.some( hasActiveStatus ), [ uploads ] );
	const prevHadActiveUploads = usePrevious( hasActiveUploads );

	const hasReviewerDuplicates = useMemo( () => (
		assetUploads.some( u => u.errorName === ASSET_UPLOAD_ERRORS.REVIEWER_DUPLICATE_ASSET )
	), [ assetUploads ] );

	const dispatch = useDispatch();
	const onCancelClick = useCallback(
		( uploadID: number ) => dispatch( cancelAssetUpload( uploadID ) ),
		[ dispatch ]
	);

	const { mutate: requestUpgradeRole } = useRequestUpgradeRoleMutation();
	const onRequestEditorAccess = useCallback( () => {
		requestUpgradeRole( { projectID, source: UPGRADE_REQUEST_SOURCES.REVIEWER_ROUND_UPLOAD } );
	}, [ requestUpgradeRole, projectID ] );

	useEffect( () => {
		if ( hasActiveUploads && !prevHadActiveUploads ) {
			expandableRef.current?.open();
		}
	}, [ hasActiveUploads ] );

	return (
		<Expandable ref={expandableRef} title={project?.name || 'Project'} initiallyOpened={hasActiveUploads}>
			{hasReviewerDuplicates && (
				<Stack
					spacing={0}
					px={12}
					py={10}
					mx={12}
					mt={8}
					style={{ background: 'var(--color-feedback-on-warning)', borderRadius: 4 }}
				>
					<Text variant="footnote" style={{ color: 'var(--color-feedback-warning)', lineHeight: '20px' }}>
						Your role doesn&apos;t allow uploading and stacking new rounds. Rename your assets before uploading or{' '}
						<Anchor onClick={onRequestEditorAccess} style={{ color: 'var(--color-feedback-warning)', textDecoration: 'underline', cursor: 'pointer' }}>
							Request Editor access.
						</Anchor>
					</Text>
				</Stack>
			)}
			{uploads.map( ( upload, index ) => (
				<>
					<UploadItem key={upload.id} upload={upload} onCancelClick={onCancelClick} />
					{index < ( uploads.length - 1 ) && (
						<Divider size="sm" sx={{ borderColor: 'var(--color-outline-light)' }} />
					)}
				</>
			) )}
		</Expandable>
	);
};

export default ProjectUploadsList;
